import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { Easings, tween } from '../../core/Tween';
import { columnLetterFor } from './generateBingoCard';

/** Asset aliases registered by the host theme — engine widget checks
 * each before falling back to the drawn-Graphics chrome. */
const CALLER_CAGE_ALIAS = 'bingo:caller_cage';
const CALLER_PLATE_ALIAS = 'bingo:caller_plate';
const BALL_ALIAS = 'bingo:ball';

/**
 * Centred display for the most recently called number — big "B-12" plate that
 * pops in (easeOutBack), holds, then sits at half-opacity until the next call
 * replaces it. A small history strip below shows the last 6 numbers called.
 */
export class NumberCaller extends Container {
  private callText: Text;
  private historyContainer: Container;
  private historyEntries: Container[] = [];
  /** True when the {@code bingo:ball} texture is registered — the
   * history strip then renders ball Sprites in place of the drawn
   * pill chips. */
  private readonly useBallSprites: boolean;

  constructor(plateWidth: number = 240) {
    super();

    // Optional commissioned cage backdrop — sits behind the plate as
    // ambient chrome (the "where the numbers come from" art).
    const cageTex = AssetLoader.shared.getTexture(CALLER_CAGE_ALIAS);
    if (cageTex) {
      const cage = new Sprite(cageTex);
      cage.anchor.set(0.5);
      const aspect = cageTex.width / cageTex.height;
      // 1.4× the plate height so the cage frames the call display
      // without pushing the history strip off-screen.
      cage.height = 104 * 1.4;
      cage.width = cage.height * aspect;
      cage.alpha = 0.85;
      this.addChild(cage);
    }

    const plateTex = AssetLoader.shared.getTexture(CALLER_PLATE_ALIAS);
    if (plateTex) {
      const plate = new Sprite(plateTex);
      plate.anchor.set(0.5);
      const aspect = plateTex.width / plateTex.height;
      // Force the height so layout maths upstream (which assumes a
      // 104px plate) keeps working. Width follows the asset aspect.
      plate.height = 104;
      plate.width = Math.max(plateWidth, 104 * aspect);
      this.addChild(plate);
    } else {
      const callPlate = new Graphics();
      this.addChild(callPlate);
      callPlate
        .roundRect(-plateWidth / 2, -52, plateWidth, 104, 22)
        .fill({ color: 0x1a0830, alpha: 0.9 })
        .stroke({ color: 0xff6bd6, alpha: 0.85, width: 3 });
    }

    this.callText = new Text({
      text: '—',
      style: {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 56,
        fontWeight: '900',
        fill: 0xfff59d,
        letterSpacing: 2,
        align: 'center',
      },
    });
    this.callText.anchor.set(0.5);
    this.addChild(this.callText);

    this.useBallSprites = AssetLoader.shared.getTexture(BALL_ALIAS) !== null;
    this.historyContainer = new Container();
    this.historyContainer.y = 84;
    this.addChild(this.historyContainer);
  }

  /** Display a new called number with a pop animation. */
  call(number: number): void {
    const letter = columnLetterFor(number);
    this.callText.text = `${letter}-${number}`;
    this.callText.alpha = 0;
    this.callText.scale.set(0.5);

    tween({
      durationMs: 240,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.callText.alpha = p;
        this.callText.scale.set(0.5 + p * 0.5);
      },
    });

    // Add to history strip (oldest gets shifted left, max 6 visible).
    const entry = this.makeHistoryEntry(letter, number);
    this.historyContainer.addChild(entry);
    this.historyEntries.unshift(entry);

    while (this.historyEntries.length > 6) {
      const old = this.historyEntries.pop();
      if (old) {
        old.destroy();
      }
    }
    this.layoutHistory();
  }

  private makeHistoryEntry(letter: string, number: number): Container {
    const entry = new Container();
    if (this.useBallSprites) {
      // Image mode: render the commissioned ball sprite (a single
      // master texture; we'd vary the colour per BINGO column if the
      // brief shipped per-letter variants — for now all balls share
      // the master art).
      const tex = AssetLoader.shared.getTexture(BALL_ALIAS);
      if (tex) {
        const ball = new Sprite(tex);
        ball.anchor.set(0.5);
        const aspect = tex.width / tex.height;
        // 36px diameter — slightly larger than the drawn pill so the
        // painted ball reads cleanly.
        ball.height = 36;
        ball.width = 36 * aspect;
        entry.addChild(ball);
      }
    } else {
      const bg = new Graphics();
      bg
        .roundRect(-22, -16, 44, 32, 16)
        .fill({ color: 0x6b3d77, alpha: 0.7 })
        .stroke({ color: 0xffffff, alpha: 0.25, width: 1 });
      entry.addChild(bg);
    }
    const text = new Text({
      text: `${letter}${number}`,
      style: {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 13,
        fontWeight: '700',
        // Painted balls have their own colour; force white so the
        // overlaid letter-number reads in either mode.
        fill: 0xffffff,
        // Faint shadow gives the text a halo over the ball texture
        // (where it would otherwise blend into the painted highlight).
        stroke: this.useBallSprites
          ? { color: 0x000000, alpha: 0.5, width: 2 }
          : undefined,
      },
    });
    text.anchor.set(0.5);
    entry.addChild(text);
    return entry;
  }

  private layoutHistory(): void {
    const spacing = 50;
    const total = this.historyEntries.length;
    const startX = -((total - 1) * spacing) / 2;
    this.historyEntries.forEach((entry, i) => {
      entry.x = startX + i * spacing;
      entry.alpha = 1 - i * 0.13;
    });
  }
}
